import { useCreateWhatsAppTemplate } from '@/api';
import { Button, FormSelect, Input, Textarea } from '@/components/atoms';
import { FormField } from '@/components/molecules';
import { WHATSAPP_TEMPLATE_CATEGORIES } from '@/lib/constants/whatsapp-templates';
import {
  whatsappTemplateSchema,
  type WhatsAppTemplateFormData,
} from '@/validation-schema/whatsapp-template';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { TemplatePreview } from './template-preview';

interface TemplateFormProps {
  onSuccess: () => void;
  compact?: boolean;
}

const DEFAULT_TEMPLATE_BODY =
  'Hi {{customer_name}} 👋\n\nThanks for your order #{{order_id}}!';

const defaultValues: WhatsAppTemplateFormData = {
  title: '',
  category: 'Lead Follow-Up',
  body: DEFAULT_TEMPLATE_BODY,
  internalNote: '',
};

export const TemplateForm = ({
  onSuccess,
  compact = false,
}: TemplateFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<WhatsAppTemplateFormData>({
    resolver: yupResolver(whatsappTemplateSchema),
    mode: 'onChange',
    defaultValues,
  });

  const watched = useWatch({ control });

  const { mutate: create, isPending } = useCreateWhatsAppTemplate(onSuccess);

  const submit = (data: WhatsAppTemplateFormData) => {
    create({
      title: data.title,
      category: data.category,
      body: data.body,
      internalNote: data.internalNote?.trim() ? data.internalNote : undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className={cn(
        'grid grid-cols-1 gap-6 mt-4',
        !compact && 'lg:grid-cols-2 gap-8 mt-6'
      )}
    >
      <div className="flex flex-col gap-4">
        <FormField label="Template title" error={errors.title?.message}>
          <Input placeholder="Post-Purchase Thank You" {...register('title')} />
          <p className="text-xs text-muted-foreground mt-1">
            A display name — the system generates a slug automatically.
          </p>
        </FormField>

        <FormField label="Category" error={errors.category?.message}>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormSelect
                placeholder="Select category"
                value={field.value}
                onChange={field.onChange}
                options={WHATSAPP_TEMPLATE_CATEGORIES.map((category) => ({
                  value: category,
                  label: category,
                }))}
              />
            )}
          />
        </FormField>

        <FormField
          label="Message body"
          error={errors.body?.message}
          infoTip="{{customer_name}} is filled with the contact's name from the conversation. {{order_id}} is for their order reference (e.g. ORD-12345). Add them anywhere in your message, they'll be replaced when you send the template."
        >
          <Textarea
            placeholder="Enter your message…"
            rows={6}
            {...register('body')}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Use named placeholders like {'{{customer_name}}'} and{' '}
            {'{{order_id}}'}.
          </p>
        </FormField>

        <FormField
          label="Internal note (optional)"
          error={errors.internalNote?.message}
        >
          <Textarea
            placeholder="Best after 24h inactivity"
            rows={2}
            {...register('internalNote')}
          />
        </FormField>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          isLoading={isPending}
          disabled={!isValid}
          label="Create template"
        />
      </div>

      <div className={cn(!compact && 'lg:sticky lg:top-4 lg:self-start')}>
        <p className="text-sm font-semibold mb-3">WhatsApp preview</p>
        <TemplatePreview body={watched.body ?? ''} />
      </div>
    </form>
  );
};
